import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, X, MessageSquare, ExternalLink, Gift } from 'lucide-react';

import slimLedFrame from '../../assets/gifting/Slim Led Frame.jpg';
import tableTop from '../../assets/gifting/Table Top.jpg';
import photoFrame from '../../assets/gifting/Slim Led Frame.jpg';
import mug from '../../assets/gifting/Slim Led Frame.jpg';
import bottle from '../../assets/gifting/Slim Led Frame.jpg';
import poloTshirt from '../../assets/gifting/Slim Led Frame.jpg';
import hoodies from '../../assets/gifting/Slim Led Frame.jpg';
import roundNeck from '../../assets/gifting/Slim Led Frame.jpg';
import cap from '../../assets/gifting/Slim Led Frame.jpg';

const giftingProducts = [
  {
    id: 1,
    name: 'Slim Led Frame',
    shortDesc:
      'Elegant LED photo frame with slim design. Perfect for gifting on corporate events and special occasions.',
    imageUrl: slimLedFrame,
    category: 'Frames & Displays',
  },
  {
    id: 2,
    name: 'Table Top',
    shortDesc: 'Premium table top display stand for photos and certificates. Ideal for desk decoration.',
    imageUrl: tableTop,
    category: 'Frames & Displays',
  },
  {
    id: 3,
    name: 'Photo Frame',
    shortDesc: 'Beautiful photo frame for memories. Available in various sizes and finishes.',
    imageUrl: photoFrame,
    category: 'Frames & Displays',
  },
  {
    id: 4,
    name: 'Mug',
    shortDesc: 'Custom printed ceramic mug. Perfect for personalized gifting.',
    imageUrl: mug,
    category: 'Drinkware',
  },
  {
    id: 5,
    name: 'Bottle',
    shortDesc: 'Premium steel/plastic bottle with custom branding. Eco-friendly and durable.',
    imageUrl: bottle,
    category: 'Drinkware',
  },
  {
    id: 6,
    name: 'Polo T-shirt',
    shortDesc:
      'High-quality polo t-shirt with customization options. Available in DTF and Embroidery.',
    imageUrl: poloTshirt,
    category: 'Apparel',
    variations: ['DTF', 'Embroidery'],
  },
  {
    id: 7,
    name: 'Hoodies',
    shortDesc: 'Premium hoodies for casual wear. Perfect for corporate gifting.',
    imageUrl: hoodies,
    category: 'Apparel',
    variations: ['DTF', 'Embroidery'],
  },
  {
    id: 8,
    name: 'Round Neck T-shirt',
    shortDesc: 'Comfortable round neck t-shirt with custom printing options.',
    imageUrl: roundNeck,
    category: 'Apparel',
    variations: ['DTF', 'Embroidery'],
  },
  {
    id: 9,
    name: 'Cap',
    shortDesc: 'Stylish caps with custom logo printing. Perfect for promotional events.',
    imageUrl: cap,
    category: 'Apparel',
    variations: ['DTF', 'Embroidery'],
  },
];

function useGridReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.06, rootMargin: '0px 0px -6% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function useRowReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -6% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function GiftingProductRow({ product, index, total, onOpen }) {
  const [ref, visible] = useRowReveal();
  const imageOnRight = index % 2 === 1;
  const isSpotlight = index === 0;

  return (
    <article
      ref={ref}
      className={[
        'relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-14 items-center',
        index > 0 ? 'pt-16 md:pt-24 border-t border-white/[0.07]' : '',
      ].join(' ')}
      aria-labelledby={`gifting-product-${product.id}`}
    >
      <div
        className={[
          'lg:col-span-7',
          imageOnRight ? 'lg:col-start-6 lg:row-start-1' : 'lg:col-start-1',
          visible ? 'animate-fade-up' : 'opacity-0',
        ].join(' ')}
        style={{ animationDelay: visible ? '60ms' : undefined }}
      >
        <button
          type="button"
          onClick={() => onOpen(product)}
          className={[
            'relative group w-full text-left overflow-hidden rounded-2xl md:rounded-3xl ring-1 ring-white/10 shadow-card-premium',
            'transition-shadow duration-500 hover:shadow-card-premium-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-amber/60',
            isSpotlight ? 'min-h-[240px] sm:min-h-[280px] lg:min-h-[360px]' : 'min-h-[220px] sm:min-h-[260px] lg:min-h-[300px]',
          ].join(' ')}
        >
          <img
            src={product.imageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
          />
          <div
            className="absolute inset-0 bg-gradient-to-tr from-black/55 via-transparent to-transparent opacity-85 md:opacity-75 group-hover:opacity-95 transition-opacity pointer-events-none"
            aria-hidden
          />
          <span className="sr-only">View details for {product.name}</span>
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 pointer-events-none">
            <span className="inline-flex items-center rounded-full bg-black/55 backdrop-blur-md px-3 py-1.5 text-[11px] font-bold tracking-[0.2em] text-brand-yellow border border-white/10">
              {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-brand px-3 py-1.5 text-xs font-bold text-black shadow-lg">
              Details
              <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
            </span>
          </div>
          {isSpotlight ? (
            <span className="absolute top-4 right-4 hidden sm:inline rounded-full bg-black/45 backdrop-blur-md px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-amber border border-white/10 pointer-events-none">
              Popular pick
            </span>
          ) : null}
        </button>
      </div>

      <div
        className={[
          'lg:col-span-5 flex flex-col justify-center',
          imageOnRight ? 'lg:col-start-1 lg:row-start-1' : 'lg:col-start-8',
          visible ? 'animate-fade-up' : 'opacity-0',
        ].join(' ')}
        style={{ animationDelay: visible ? '140ms' : undefined }}
      >
        <div className="w-12 h-1 rounded-full bg-gradient-brand mb-5" aria-hidden />
        <p className="text-brand-amber/90 text-xs font-semibold uppercase tracking-[0.2em] mb-3">
          {product.category}
        </p>
        <h2
          id={`gifting-product-${product.id}`}
          className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-snug mb-4"
        >
          {product.name}
        </h2>
        <p className="text-gray-400 text-base leading-relaxed mb-4">{product.shortDesc}</p>
        {product.variations?.length ? (
          <p className="text-gray-500 text-sm mb-8">
            <span className="text-brand-yellow font-semibold">Customization:</span>{' '}
            {product.variations.join(' · ')}
          </p>
        ) : (
          <p className="text-gray-500 text-sm mb-8">Ideal for branded merch packs and event giveaways.</p>
        )}

        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          <button
            type="button"
            onClick={() => onOpen(product)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-brand px-5 py-3 text-sm font-bold text-black hover:opacity-95 transition-opacity shadow-md"
          >
            <ArrowUpRight className="w-4 h-4 shrink-0" />
            Product details
          </button>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:border-brand-amber/40 hover:bg-brand-orange/10 transition-colors"
          >
            Enquire
          </Link>
        </div>
      </div>
    </article>
  );
}

function ProductModal({ product, onClose }) {
  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gifting-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default w-full h-full border-0 bg-transparent"
        aria-label="Close dialog"
        onClick={close}
      />
      <div className="relative w-full max-w-lg rounded-2xl md:rounded-3xl border border-white/10 bg-[#0f0f0f] shadow-card-premium-hover animate-modal-in overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-40 bg-gradient-to-br from-brand-orange/10 via-transparent to-brand-yellow/5"
          aria-hidden
        />
        <div className="relative flex justify-between items-start gap-4 p-5 md:p-6 border-b border-white/10">
          <div>
            <p className="text-brand-amber text-xs font-semibold uppercase tracking-wider mb-1">
              {product.category}
            </p>
            <h3 id="gifting-modal-title" className="text-xl font-bold text-white pr-2">
              {product.name}
            </h3>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-full p-2 text-gray-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="relative p-5 md:p-6 space-y-4">
          <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 aspect-[4/3] max-h-56">
            <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">{product.shortDesc}</p>
          {product.variations?.length ? (
            <div className="rounded-xl border border-brand-amber/20 bg-brand-orange/5 px-4 py-3">
              <p className="text-brand-amber text-xs font-semibold uppercase tracking-wider mb-1">
                Options
              </p>
              <p className="text-gray-300 text-sm">{product.variations.join(' · ')}</p>
            </div>
          ) : null}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link
              to="/contact"
              onClick={close}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-brand py-3 text-sm font-bold text-black hover:opacity-95 transition-opacity"
            >
              <ExternalLink className="w-4 h-4" />
              Get a quote
            </Link>
            <a
              href="https://wa.me/919821466536"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/50 py-3 text-sm font-semibold text-emerald-400 hover:bg-emerald-500 hover:text-white transition"
            >
              <MessageSquare className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function GiftingProducts({ variant = 'default' }) {
  const isPage = variant === 'page';
  const [gridRef, gridVisible] = useGridReveal();
  const [selectedProduct, setSelectedProduct] = useState(null);

  return (
    <section
      className={[
        isPage ? 'pt-4 md:pt-8 pb-20 md:pb-32' : 'py-16 md:py-24',
        'bg-surface border-t border-white/5 relative',
      ].join(' ')}
    >
      {isPage && (
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.3] bg-[radial-gradient(ellipse_70%_40%_at_50%_0%,rgba(250,204,21,0.06),transparent_55%)]"
          aria-hidden
        />
      )}

      <div className="container mx-auto px-4 relative z-10">
        {isPage ? (
          <header className="text-center mb-12 md:mb-16 max-w-2xl mx-auto">
            <p className="inline-flex items-center justify-center gap-2 text-brand-amber text-xs md:text-sm uppercase tracking-[0.22em] font-semibold mb-3">
              <Gift className="w-4 h-4 text-brand-yellow shrink-0" aria-hidden />
              Gifting solutions
            </p>
            <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight">
              Corporate <span className="text-gradient-brand">gifting products</span>
            </h2>
            <p className="text-gray-500 text-sm md:text-base mt-4">
              Same story layout as signage categories—full-width rows, slow photo zoom, and a quick
              detail sheet for quantities and customization.
            </p>
          </header>
        ) : (
          <div className="text-center mb-12">
            <span className="text-brand-amber text-sm uppercase tracking-[0.18em] font-semibold">
              Gifting solutions
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
              Corporate <span className="text-gradient-brand">gifting products</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Make your brand memorable with our premium corporate gifts. Perfect for festivals,
              events, and client appreciation.
            </p>
          </div>
        )}

        {isPage ? (
          <div className="max-w-6xl mx-auto space-y-0">
            {giftingProducts.map((product, index) => (
              <GiftingProductRow
                key={product.id}
                product={product}
                index={index}
                total={giftingProducts.length}
                onOpen={setSelectedProduct}
              />
            ))}
          </div>
        ) : (
          <div
            ref={gridRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {giftingProducts.map((product, idx) => (
              <button
                key={product.id}
                type="button"
                onClick={() => setSelectedProduct(product)}
                className={[
                  'group text-left bg-surface-elevated rounded-2xl overflow-hidden',
                  'border border-white/10 hover:border-brand-amber/35',
                  'hover:bg-white/[0.03] transition-all duration-300',
                  'hover:shadow-[0_0_40px_-10px_rgba(234,88,12,0.35)]',
                  gridVisible ? 'animate-fade-up' : 'opacity-0',
                ].join(' ')}
                style={{ animationDelay: gridVisible ? `${idx * 70}ms` : undefined }}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity pointer-events-none"
                    aria-hidden
                  />
                </div>
                <div className="p-4">
                  <p className="text-brand-amber/80 text-[10px] font-semibold uppercase tracking-wider mb-1">
                    {product.category}
                  </p>
                  <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-brand-yellow transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-gray-400 text-sm line-clamp-2">{product.shortDesc}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedProduct && (
        <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}
    </section>
  );
}

export default GiftingProducts;
